import {navigateToTaskDetail} from 'components/router/router.function.js';
import {
    getTasksForRender, getOneHandleOnlyFlag,
    getHistoryTasksForRender, getProcessOverviewTasks, getDocumentOverviewTasks,
} from "components/store/application.reducers";
import {processDetailsPath, taskDetailsPath} from "components/router/detailsPath";

function buildIteration(tasks, oneHandleOnly) {
    const nonHandledKeys = tasks.filteredTasks
        .filter(t => !t.task.isHandled)
        .map(t => t.task.key);

    if (oneHandleOnly) {
        return nonHandledKeys;
    }
    const nonHandledKeysSet = new Set(nonHandledKeys);
    return tasks.selectedKeys.filter(k => nonHandledKeysSet.has(k));
}

// The uid routes hand us a uid, while the iteration and the selection are both keyed by task key
function toTaskKey(id, tasks) {
    const match = tasks.filteredTasks.find(t => t.task.uid === id);
    return match ? match.task.key : id;
}

function findTask(tasks, key) {
    const match = tasks.filteredTasks.find(t => t.task.key === key);
    return match ? match.task : {key};
}

export function navigateBetweenTasks(currentTaskId, isForward) {
    return function (dispatch, getState) {
        const state = getState();
        const tasks = getTasksForRender(state);
        const iteration = buildIteration(tasks, getOneHandleOnlyFlag(state));
        const wantedId = getNextTaskId(iteration, toTaskKey(currentTaskId, tasks), isForward, state);
        return wantedId !== null ? taskDetailsPath(findTask(tasks, wantedId)) : "";
    };
}

export function navigateBetweenSignableTasks(currentTaskKey, isForward) {
    return function (dispatch, getState) {
        const state = getState();
        const tasks = getTasksForRender(state);
        const iteration = buildIteration(tasks, getOneHandleOnlyFlag(state));
        const wantedId = getNextTaskId(iteration, currentTaskKey, isForward, state);
        return wantedId !== null ? navigateToTaskDetail(wantedId) : "";
    };
}

export function navigateBetweenHistoryTasks(currentTaskKey, isForward) {
    return function (dispatch, getState) {
        const state = getState();
        const iteration = getHistoryTasksForRender(state).allTasks.map(t => t.key);
        const wantedId = getNextTaskId(iteration, currentTaskKey, isForward, state);
        return wantedId !== null ? "../taskhistory/" + wantedId : "";
    };
}

export function navigateBetweenProcessTasks(currentTaskId, isForward) {
    return function (dispatch, getState) {
        const state = getState();
        const allTasks = getProcessOverviewTasks(state).allTasks;
        const current = allTasks.find(t => t.uid === currentTaskId);
        const iteration = allTasks.map(t => t.key);
        const wantedId = getNextTaskId(iteration, current ? current.key : currentTaskId, isForward, state);
        if (wantedId === null) {
            return "";
        }
        return processDetailsPath(allTasks.find(t => t.key === wantedId) || {key: wantedId});
    };
}

export function navigateBetweenDocuments(currentDocumentKey, isForward) {
    return function (dispatch, getState) {
        const state = getState();
        const iteration = getDocumentOverviewTasks(state).rows.map(t => t.key);
        const wantedId = getNextTaskId(iteration, currentDocumentKey, isForward, state);
        return wantedId !== null ? "../document/" + wantedId : "";
    };
}

function getNextTaskId(iteration, currentTaskKey, isForward, state) {
    if (isForward && isLastInTheIteration(currentTaskKey, iteration)) return null;
    if (!isForward && _isFirstInTheIteration(currentTaskKey, iteration)) return null;

    let currIndex = iteration.indexOf(currentTaskKey);

    // Current task may be handled and absent from the filtered iteration.
    // Find its position in the full ordered list and pick the nearest valid neighbour.
    if (currIndex === -1) {
        const allKeys = getTasksForRender(state).filteredTasks.map(t => t.task.key);
        const posInAll = allKeys.indexOf(currentTaskKey);
        if (posInAll === -1) return null;

        const candidates = isForward ? allKeys.slice(posInAll + 1) : allKeys.slice(0, posInAll).reverse();
        const iterationSet = new Set(iteration);
        return candidates.find(k => iterationSet.has(k)) ?? null;
    }

    return iteration[currIndex + (isForward ? 1 : -1)];
}

export function isLastInTheIteration(taskKey, filteredKeys) {
    return filteredKeys[filteredKeys.length - 1] === taskKey;
}

function _isFirstInTheIteration(taskKey, filteredKeys) {
    return filteredKeys[0] === taskKey;
}
